import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
  DragOverEvent,
  MeasuringStrategy,
  rectIntersection,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { SortableItem } from "../../components/SortableItem";
import { DroppableContainer } from "../../components/DroppableContainer";
import {
  Order,
  OrderProspectPhase,
  OrderStatus,
  getProspectPhaseDisplayName,
} from "../../types/order";
import { ProspectCard } from "./ProspectCard";
import { Box } from "@mui/material";
import { useApiRequest } from "../../hooks/useApiRequest";
import { FillContainer } from "../../components/FillContainer";
import { useGlobalToastNotificationContext } from "../../context/GlobalToastNotificationContext";

type ProspectKanbanViewProps = {
  orders: Order[];
  refreshOrders: () => void;
  handleOrderEditClick: (orderGuid: string) => void;
  handleDeleteOrderClick: (orderGuid: string) => void;
  handleConvertToQuoteClick: (prospectGuid: string) => void;
  usingLastUpdated: boolean;
};

export const ProspectKanbanView: React.FC<ProspectKanbanViewProps> = ({
  orders,
  refreshOrders,
  handleOrderEditClick,
  handleDeleteOrderClick,
  handleConvertToQuoteClick,
  usingLastUpdated,
}) => {
  const [items, setItems] = useState<Record<string, Order[]>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const { showErrorToast } = useGlobalToastNotificationContext();
  
  const {
    request: updateRequest,
    data: updateData,
    error: updateError,
    errorMessage: updateErrorMessage,
  } = useApiRequest<Order>();

  const updateCounts = useCallback(() => {
    setCounts(
      Object.entries(items).reduce(
        (acc, [key, value]) => ({ ...acc, [key]: value.length }),
        {}
      )
    );
  }, [items]);

  type ValidKey = Exclude<OrderProspectPhase | OrderStatus, null | "p" | "s" | "q" | "o" | "f" | "i" | "c" | "v">;

  const transformOrders = useCallback(() => {
    const newItems: Partial<Record<ValidKey, Order[]>> = {
      ppn: [],
      ppc: [],
      ppw: [],
      pph: [],
      ppx: [],      
      a: [],
    };

    orders.forEach((order) => {
      if (order.prospectPhase !== null || order.orderStatus === "a") {
        const os = order.orderStatus === "p" ? order.prospectPhase : order.orderStatus;

        if (isValidKey(os)) {
          if (!newItems[os]) {
            newItems[os] = [];
          }
          newItems[os]?.push(order);
        }
      }
    });

    function isValidKey(key: any): key is ValidKey {
      return ["ppn", "ppc", "ppw", "pph", "ppx", "a"].includes(key);
    }

    setItems(newItems);
    setCounts(
      Object.entries(newItems).reduce(
        (acc, [key, value]) => ({ ...acc, [key]: value.length }),
        {}
      )
    );
  }, [orders]);

  const orderedItems = useMemo(() => {
    const oi = Object.entries(items).reduce((acc, [key, value]) => {
      // Prospect status goes by phase
      return {
        ...acc,
        [key]: usingLastUpdated ? value : value.sort((a, b) => a.createdTimestamp - b.createdTimestamp),
      };
    }, {});
    return oi;
  }, [items, usingLastUpdated]);

  useEffect(() => {
    if (!updateData) {
      return;
    }
    refreshOrders();
  }, [refreshOrders, updateData]);

  useEffect(() => {
    transformOrders();
  }, [transformOrders, orders]);

  useEffect(() => {
    if (updateError?.response?.status === 400) {
      refreshOrders();
      showErrorToast({ message: updateErrorMessage ?? "Error updating prospect status" });
    }
  }, [updateError, refreshOrders, updateErrorMessage, showErrorToast]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const findContainer = (id: UniqueIdentifier) => {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find((key) =>
      items[key].some((item) => item.orderGuid === id)
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const { id } = active;

    setActiveId(id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    const { id } = active;
    const overId = over?.id;

    if (!overId) {
      return;
    }

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer) {
      return;
    }

    if (activeContainer !== overContainer) {
      setItems((prev) => {
        const activeItems = prev[activeContainer];
        const overItems = prev[overContainer];

        const activeIndex = activeItems.findIndex(
          (item) => item.orderGuid === id
        );
        const overIndex = overItems.findIndex(
          (item) => item.orderGuid === overId
        );

        let newIndex;
        if (overId in prev) {
          newIndex = overItems.length + 1;
        } else {
          const isBelowLastItem =
            over &&
            overIndex === overItems.length - 1 &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height;
          const modifier = isBelowLastItem ? 1 : 0;

          newIndex =
            overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        }

        return {
          ...prev,
          [activeContainer]: [
            ...prev[activeContainer].filter((item) => item.orderGuid !== id),
          ],
          [overContainer]: [
            ...prev[overContainer].slice(0, newIndex),
            activeItems[activeIndex],
            ...prev[overContainer].slice(newIndex, prev[overContainer].length),
          ],
        };
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const { id } = active;
    const overId = over?.id;

    if (!overId) {
      setActiveId(null);
      return;
    }

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer) {
      setActiveId(null);
      return;
    }

    if (activeContainer !== overContainer) {
      const activeIndex = items[activeContainer].findIndex(
        (item) => item.orderGuid === id
      );
      const overIndex = items[overContainer].findIndex(
        (item) => item.orderGuid === overId
      );

      if (activeIndex !== overIndex) {
        setItems((items) => ({
          ...items,
          [overContainer]: arrayMove(
            items[overContainer],
            activeIndex,
            overIndex
          ),
        }));
      }
    }

    if (overContainer === "a") {
      // archiving
      updateRequest(`/orders/${id}/edit`, {
        method: "POST",
        data: {
          orderStatus: overContainer,
        },
      });
    } else {
      // changing prospect phase
      updateRequest(`/orders/${id}/edit`, {
        method: "POST",
        data: {
          prospectPhase: overContainer,
          orderStatus: "p",
        },
      });
    }

    setActiveId(null);
    updateCounts();
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <FillContainer>
          <Box
            sx={{
              display: "flex",
              paddingBottom: 2,
              paddingX: 3,
            }}
          >
            {Object.keys(orderedItems).map((containerId) => (
              <DroppableContainer
                key={containerId}
                id={containerId}
                heading={containerId === "a" ? "Archived" : getProspectPhaseDisplayName(containerId as OrderProspectPhase)}
                count={counts[containerId]}
                isOver={
                  activeId ? containerId === findContainer(activeId) : false
                }
              >
                <SortableContext
                  items={items[containerId].map((item) => item.orderGuid)}
                  strategy={horizontalListSortingStrategy}
                >
                  {items[containerId].map((item) => (
                    <SortableItem key={item.orderGuid} id={item.orderGuid}>
                      <ProspectCard
                        order={item}
                        handleOrderEditClick={handleOrderEditClick}
                        handleDeleteOrderClick={handleDeleteOrderClick}
                        handleConvertToQuoteClick={handleConvertToQuoteClick}
                      />
                    </SortableItem>
                  ))}
                </SortableContext>
              </DroppableContainer>
            ))}
          </Box>
        </FillContainer>
        <DragOverlay>
          {activeId ? (
            <Box sx={{ opacity: 0.9 }}>
              <SortableItem id={activeId}>
                <ProspectCard
                  order={
                    items[findContainer(activeId) as string].find(
                      (item) => item.orderGuid === activeId
                    )!
                  }
                  handleOrderEditClick={handleOrderEditClick}
                  handleDeleteOrderClick={handleDeleteOrderClick}
                  handleConvertToQuoteClick={handleConvertToQuoteClick}
                />
              </SortableItem>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
};
